import { IconName } from '@blueprintjs/icons';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import Box from 'ui-box';
import { Heading, majorScale } from '.';
import { Button } from './Button';
import { Card } from './Card';
import { Icon } from './Icon';
import { getColorFromIntent, Intents } from './Intents';
import { Portal } from './Portal';

export interface IAlert {
  intent?: Intents;
  icon?: IconName;
  title?: string;
  message: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  overlayCanCancel?: boolean;
  hideConfirm?: boolean;
}

interface IAlertProps extends IAlert {
  onClosed?: () => void;
  onCancel?: () => Promise<any>;
  onConfirm?: () => Promise<any>;
}

/**
 * The actual alert instance
 */
export const Alert: React.FC<IAlertProps> = ({
  intent = 'info',
  icon,
  title,
  message,
  cancelButtonText,
  confirmButtonText,
  overlayCanCancel = true,
  hideConfirm,
  onCancel,
  onConfirm,
  onClosed,
}) => {
  const intl = useIntl();

  const alertRef = useRef<HTMLDivElement>(null);

  const [shown, setShown] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useLayoutEffect(() => {
    setShown(true);
  }, []);

  const close = () => {
    setShown(false);
    setConfirming(false);
    setCancelling(false);
  };

  const cancel = async () => {
    if (confirming || cancelling) return;
    setCancelling(true);
    try {
      onCancel && (await onCancel());
    } catch (err) {
      throw err;
    } finally {
      setCancelling(false);
      close();
    }
  };

  const confirm = async () => {
    if (confirming || cancelling) return;
    setConfirming(true);
    try {
      onConfirm && (await onConfirm());
    } catch (err) {
      throw err;
    } finally {
      setConfirming(false);
      close();
    }
  };

  const maybeCancel = () => {
    if (overlayCanCancel) {
      cancel();
    }
  };

  return (
    <CSSTransition
      timeout={150}
      in={shown}
      onExited={() => {
        // We should remove this alert - as we must be done with it...
        onClosed && onClosed();
      }}
      onEntered={() => {}}
      className="dialog-container"
      nodeRef={alertRef}
      unmountOnExit
    >
      <Box ref={alertRef}>
        <div className="overlay" onClick={() => maybeCancel()} />
        <Card elevation={3} className="dialog p-4" width={384}>
          <Box
            display="grid"
            gridTemplateColumns={icon ? '32px 1fr' : '1fr'}
            columnGap={majorScale(2)}
          >
            {icon && (
              <Box>
                <Icon
                  icon={icon}
                  size={32}
                  color={getColorFromIntent(intent)}
                />
              </Box>
            )}
            <Box>
              {title && (
                <Heading size={300} marginBottom={majorScale(1)}>
                  {title}
                </Heading>
              )}
              <p>{message}</p>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            paddingTop={majorScale(2)}
          >
            <Button
              onClick={() => cancel()}
              disabled={confirming}
              isLoading={cancelling}
            >
              {cancelButtonText ||
                intl.formatMessage({
                  id: 'defaults.cancel',
                  defaultMessage: 'Cancel',
                })}
            </Button>
            {!hideConfirm && (
              <Button
                primary
                intent={intent}
                marginLeft={majorScale(1)}
                onClick={() => confirm()}
                disabled={cancelling}
                isLoading={confirming}
              >
                {confirmButtonText ||
                  intl.formatMessage({
                    id: 'defaults.confirm',
                    defaultMessage: 'Confirm',
                  })}
              </Button>
            )}
          </Box>
        </Card>
      </Box>
    </CSSTransition>
  );
};

export const useAlert = (options: IAlert) => {
  const show = useCallback(
    (
      onConfirm?: () => Promise<any>,
      onCancel?: () => Promise<any>,
      overwrite?: Partial<IAlert>
    ) => {
      const container = document.createElement('div');
      document.body.appendChild(container);
      container.classList.add('alerts');

      const alert = { ...options, ...overwrite };
      const component = (
        <Portal>
          <Alert
            {...alert}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onClosed={() => {
              document.body.removeChild(container);
            }}
          />
        </Portal>
      );
      ReactDOM.render(component, container);
    },
    [options]
  );

  return {
    show,
  };
};
